import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { EventModel } from '@app/events/shared/event.model';
import { FormArray, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { EventProductItemModel } from '@app/events/shared/event-product-item.model';
import { EventProductItemVariantSelectionRequest } from '@app/events/shared/event-product-item-variant-selection.model';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { TextareaComponent } from '@app/partials/form/textarea/textarea.component';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { Edit, Calendar, Plus, Trash2 } from 'lucide-angular';
import { CommonModule, NgFor, KeyValuePipe } from '@angular/common';
import { ProductService } from '@app/products/shared/product.service';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { LucideAngularModule } from 'lucide-angular';
import { ProductItemPricingComponent } from '@app/events/shared/product-item-pricing.component';
import { VariantPricingLabelComponent } from '@app/events/shared/variant-pricing-label.component';
import { ProductVariantModel } from '@app/products/shared/product-variant.model';
import { ProductVariantOptionModel } from '@app/products/shared/product-variant-option.model';

@Component({
  selector: 'app-event-product-item-edit-form',
  templateUrl: './product-item-edit-form.component.html',
  styleUrls: [],
  providers: [provideFlatpickrDefaults()],
  imports: [TextInputComponent, TextareaComponent, ButtonComponent, SelectComponent, CommonModule, LucideAngularModule, ReactiveFormsModule, ProductItemPricingComponent, VariantPricingLabelComponent, KeyValuePipe],
  standalone: true,
})
export class ProductItemEditFormComponent implements OnInit {
  @Input() public event!: EventModel;
  @Input() public productItem!: EventProductItemModel;
  @Input() showTimelineSelector = false;
  @Input() timelineItemChoices: { value: number | null; label: string }[] = [];
  @Input() set resetProcessing(value: boolean) {
    if (value) {
      this.processing = false;
    }
  }
  @Input() set resetProcessingToken(value: number) {
    if (value !== this._resetToken) {
      this.processing = false;
      this._resetToken = value;
    }
  }

  @Output() itemUpdated = new EventEmitter<{ data: any; id: number }>();
  @Output() formCancelled = new EventEmitter<void>();

  protected readonly EditIcon = Edit;
  protected readonly Calendar = Calendar;
  protected readonly Plus = Plus;
  protected readonly Trash2 = Trash2;

  protected processing = false;
  private nextUniqueId = 0;
  private _resetToken = 0;

  protected form: FormGroup<{
    quantity: FormControl<number | null>;
    amount: FormControl<number | null>;
    is_paid: FormControl<boolean | null>;
    event_timeline_item_id: FormControl<number | null>;
    variant_selections: FormArray<FormGroup<{
      _uniqueId: FormControl<number>;
      variant_id: FormControl<number>;
      variant_option_id: FormControl<number | null>;
      quantity: FormControl<number>;
      notes: FormControl<string | null>;
    }>>;
  }>;


  constructor(private productService: ProductService) {
    this.form = new FormGroup({
      quantity: new FormControl<number | null>(null),
      amount: new FormControl<number | null>(null),
      // VG-281: TEMPORARILY CHANGED - Paid status feature (2025-09-30): Changed default from true to false
      // To revert: Change back to `new FormControl<boolean | null>(true)`
      is_paid: new FormControl<boolean | null>(false),
      event_timeline_item_id: new FormControl<number | null>(null),
      variant_selections: new FormArray<any>([]),
    });

  }

  ngOnInit(): void {
    // Initialize form with existing values
    this.form.patchValue({
      quantity: this.productItem.quantity,
      amount: this.productItem.amount,
      is_paid: this.productItem.is_paid,
      event_timeline_item_id: this.productItem.event_timeline_item_id ?? this.productItem.event_timeline_item?.id ?? null,
    });

    // VG-395: For variant-based products, quantity is not used (each variant has its own quantity)
    if (this.productItem.product.is_variant_based) {
      this.form.controls.quantity.clearValidators();
      this.form.controls.amount.clearValidators();
    } else {
      // Set validators based on product type
      if (this.productItem.product.price) {
        this.form.controls.quantity.setValidators([Validators.required]);
        this.form.controls.amount.clearValidators();
      } else {
        this.form.controls.amount.setValidators([Validators.required]);
        this.form.controls.quantity.clearValidators();
      }
    }

    // Update validation status
    this.form.controls.quantity.updateValueAndValidity();
    this.form.controls.amount.updateValueAndValidity();

    // Initialize variant structure
    this.initializeVariantStructure();
  }

  private initializeVariantStructure(): void {
    // Track which variants already have selections
    const variantsWithSelections = new Set<number>();

    // VG-396: Load ALL existing variant selections (there can be multiple per variant)
    if (this.productItem.variant_selections && this.productItem.variant_selections.length > 0) {
      this.productItem.variant_selections.forEach(selection => {
        const variant = this.getVariantById(selection.variant_id);
        if (!variant) return;

        variantsWithSelections.add(selection.variant_id);

        // VG-395: variant_option_id is optional (Standard = no option = £0)
        const optionValidators: any[] = [];

        // Create form controls for existing selection
        const variantOptionControl = new FormControl<number | null>(selection.variant_option_id ?? null, optionValidators);

        // VG-381: Disable variant_option_id control if variant has no options
        if (!variant.options || variant.options.length === 0) {
          variantOptionControl.disable();
        }

        const variantForm = new FormGroup({
          _uniqueId: new FormControl<number>(this.nextUniqueId++, { nonNullable: true }),
          variant_id: new FormControl<number>(selection.variant_id!, { nonNullable: true }),
          variant_option_id: variantOptionControl,
          quantity: new FormControl<number>(selection.quantity || 0, { nonNullable: true, validators: [Validators.min(0)] }),
          notes: new FormControl<string | null>(selection.notes ?? null),
        });

        // Listen for variant_option_id changes and reset quantity when cleared
        variantForm.controls.variant_option_id.valueChanges.subscribe(value => {
          if (value === null) {
            // Reset quantity to 0 when variant option is cleared
            variantForm.controls.quantity.setValue(0);
          } else if (variantForm.controls.quantity.value === 0) {
            // Set quantity to 1 when a variant option is selected after being cleared
            variantForm.controls.quantity.setValue(1);
          }
          this.applyMaxValidator(variantForm);
          this.clearServerErrorOnControl(variantForm);
        });

        variantForm.controls.quantity.valueChanges.subscribe(() => this.clearServerErrorOnControl(variantForm));
        this.applyMaxValidator(variantForm);
        (this.form.controls.variant_selections as any).push(variantForm);
      });
    }

    // Auto-create selections for 0-option variants that don't have selections yet (accommodation case)
    if (this.productItem.product?.variants) {
      this.productItem.product.variants.forEach(variant => {
        if ((!variant.options || variant.options.length === 0) && !variantsWithSelections.has(variant.id!)) {
          // Create form group with quantity=1 and no option
          const variantGroup = new FormGroup({
            _uniqueId: new FormControl<number>(this.nextUniqueId++, { nonNullable: true }),
            variant_id: new FormControl<number>(variant.id!, { nonNullable: true }),
            variant_option_id: new FormControl<number | null>(null),
            quantity: new FormControl<number>(1, { nonNullable: true }),
            notes: new FormControl<string | null>(null),
          });
          this.form.controls.variant_selections.push(variantGroup);
        }
      });
    }
  }

  // VG-396: Add a new variant option selection for a specific variant
  addVariantOption(variant: ProductVariantModel): void {
    // VG-395: variant_option_id is optional (Standard = no option = £0)
    const optionValidators: any[] = [];

    // VG-381: Create form controls for variants, even those without options
    const variantOptionControl = new FormControl<number | null>(null, optionValidators);

    // VG-381: Disable variant_option_id control if variant has no options
    if (!variant.options || variant.options.length === 0) {
      variantOptionControl.disable();
    }

    const variantGroup = new FormGroup({
      _uniqueId: new FormControl<number>(this.nextUniqueId++, { nonNullable: true }),
      variant_id: new FormControl<number>(variant.id!, { nonNullable: true }),
      variant_option_id: variantOptionControl,
      // VG-396: Default quantity to 1 for new selections (user can adjust)
      quantity: new FormControl<number>(1, { nonNullable: true, validators: [Validators.min(0)] }),
      notes: new FormControl<string | null>(null),
    });

    // Listen for variant_option_id changes
    variantGroup.controls.variant_option_id.valueChanges.subscribe(value => {
      if (value === null) {
        variantGroup.controls.quantity.setValue(0);
      } else if (variantGroup.controls.quantity.value === 0) {
        variantGroup.controls.quantity.setValue(1);
      }
      this.applyMaxValidator(variantGroup);
      this.clearServerErrorOnControl(variantGroup);
    });

    variantGroup.controls.quantity.valueChanges.subscribe(() => this.clearServerErrorOnControl(variantGroup));
    this.applyMaxValidator(variantGroup);
    this.form.controls.variant_selections.push(variantGroup);
    // Mark the form as dirty so the Update button becomes active
    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => this.form.markAsDirty());
  }

  // VG-396: Remove a variant option selection by index
  removeVariantOption(index: number): void {
    this.form.controls.variant_selections.removeAt(index);
    // Mark the form as dirty so the Update button becomes active
    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => this.form.markAsDirty());
  }

  // VG-396: Group variant selections by variant for template display
  getVariantSelectionsByVariant(): Map<number, { variant: ProductVariantModel, indices: number[] }> {
    const groupedMap = new Map<number, { variant: ProductVariantModel, indices: number[] }>();

    // Group existing selections by variant_id
    this.form.controls.variant_selections.controls.forEach((control, index) => {
      const variantId = control.value.variant_id!;
      const variant = this.getVariantById(variantId);

      if (variant) {
        if (!groupedMap.has(variantId)) {
          groupedMap.set(variantId, { variant, indices: [] });
        }
        groupedMap.get(variantId)!.indices.push(index);
      }
    });

    // Add variants that have no selections yet (so we can show "Add" button)
    this.productItem.product.variants?.forEach(variant => {
      if (!groupedMap.has(variant.id!)) {
        groupedMap.set(variant.id!, { variant, indices: [] });
      }
    });

    return groupedMap;
  }

  // Helper methods for the template
  get variantSelections(): FormArray {
    return this.form.controls.variant_selections;
  }

  get hasVariants(): boolean {
    return this.productItem.product.variants !== undefined &&
           this.productItem.product.variants.length > 0;
  }

  get isVariantBased(): boolean {
    return this.productItem.product.is_variant_based === true;
  }

  // Helper to check if variant has no options (accommodation case)
  hasNoOptions(variant: ProductVariantModel): boolean {
    return !variant.options || variant.options.length === 0;
  }

  getVariantById(variantId: number): ProductVariantModel | undefined {
    return this.productItem.product.variants?.find(v => v.id === variantId);
  }

  getVariantOptionsChoices(variantId: number): { value: number | null; label: string }[] {
    const variant = this.getVariantById(variantId);

    // VG-381: If variant has no options, return just the standard option
    if (!variant?.options || variant.options.length === 0) {
      return [{ value: null, label: 'Standard (no upgrade)' }];
    }

    const choices: { value: number | null; label: string }[] = variant.options.map((option: any) => {
      let label = option.name;

      // VG-386: Find matching selection to get event-specific pricing (effective_price includes daily pricing)
      const matchingSelection = this.productItem.variant_selections?.find(
        vs => vs.variant_id === variantId && vs.variant_option_id === option.id
      );

      // Use effective_price if available (includes daily pricing), otherwise fall back to base price
      const effectivePrice = matchingSelection?.variant_option?.['effective_price'] ?? option.price ?? 0;
      const category = option.category || 'Full Price';
      const categoryLabel = category === 'Complimentary' ? 'Complimentary' : category === 'Half Price' ? 'Half Price' : 'Full Price';
      label += ` (${categoryLabel} – £${effectivePrice})`;

      // VG-386: Mark discontinued options - check if option has daily prices but not for this event
      // If option is currently selected, use the event-specific flag from variant_selections
      // If option is not selected, check if it has daily prices configured
      const isDiscontinued = matchingSelection
        ? matchingSelection.variant_option?.is_using_daily_price === false
        : option.has_daily_prices === true; // Options with daily prices but not selected are likely discontinued

      if (isDiscontinued) {
        label += ' Discontinued';
      }

      return {
        value: option.id,
        label: label
      };
    });

    if (this.shouldIncludeStandardVariantChoice()) {
      choices.unshift({ value: null, label: 'Standard (no upgrade)' });
    }

    return choices;
  }

  private shouldIncludeStandardVariantChoice(): boolean {
    return this.productItem.product.category !== 'Complimentary Items';
  }

  getSelectedOption(variantId: number, optionId: number | null): any {
    if (!optionId) return null;
    const variant = this.getVariantById(variantId);
    return variant?.options?.find(o => o.id === optionId);
  }

  isOptionDiscontinued(option: any): boolean {
    // An option is discontinued if it has daily pricing capability but no price is set for this event date
    return option.has_daily_prices === true && option.is_using_daily_price === false;
  }

  getVariantOptionControl(selectionControl: any): any {
    return selectionControl.get('variant_option_id');
  }

  getQuantityControl(selectionControl: any): FormControl<number> {
    return selectionControl.get('quantity') as FormControl<number>;
  }

  getNotesControl(selectionControl: any): any {
    return selectionControl.get('notes');
  }

  getSelectionMax(selectionControl: FormGroup): number | undefined {
    const variantId = selectionControl.get('variant_id')?.value;
    const optionId = selectionControl.get('variant_option_id')?.value;
    if (!optionId || !variantId) return undefined;
    const variant = this.getVariantById(variantId);
    const option = variant?.options?.find((opt) => opt.id === optionId);
    const max = (option as any)['max_quantity'];
    if (max === undefined || max === null) {
      return undefined;
    }
    return max;
  }

  getSelectionCapHelp(selectionControl: FormGroup): string {
    const max = this.getSelectionMax(selectionControl);
    if (!max) return '';
    const variantId = selectionControl.get('variant_id')?.value;
    const optionId = selectionControl.get('variant_option_id')?.value;
    const variant = variantId ? this.getVariantById(variantId) : undefined;
    const option = variant?.options?.find((opt) => opt.id === optionId);
    const label = option?.name || 'this option';
    return `Max ${max} for ${label}`;
  }

  getOptionInfo(selectionControl: FormGroup): string {
    const optionId = selectionControl.get('variant_option_id')?.value;
    const variantId = selectionControl.get('variant_id')?.value;
    if (!optionId || !variantId) return '';
    const variant = this.getVariantById(variantId);
    const option = variant?.options?.find((opt) => opt.id === optionId);
    return option?.info_message || '';
  }

  private clearServerErrorOnControl(ctrl: FormGroup): void {
    const qty = ctrl.get('quantity') as FormControl<number>;
    if (!qty) return;
    if (qty.errors && qty.errors['server']) {
      const { server, ...rest } = qty.errors;
      qty.setErrors(Object.keys(rest).length ? rest : null);
    }
  }

  private applyMaxValidator(ctrl: FormGroup): void {
    const optionId = ctrl.get('variant_option_id')?.value;
    const variantId = ctrl.get('variant_id')?.value;
    const quantityCtrl = ctrl.get('quantity') as FormControl<number>;
    if (!quantityCtrl) return;
    const validators = [Validators.min(0)];

    if (optionId && variantId) {
      const variant = this.getVariantById(variantId);
      const option = variant?.options?.find((opt) => opt.id === optionId);
      const max = (option as any)?.['max_quantity'];
      if (max !== undefined && max !== null) {
        validators.push(Validators.max(max));
        const current = quantityCtrl.value ?? 0;
        if (current > max) {
          quantityCtrl.setValue(max, { emitEvent: false });
        }
      }
    }

    quantityCtrl.setValidators(validators);
    quantityCtrl.updateValueAndValidity({ emitEvent: false });
  }

  // Simplified helper method to determine if we should show the pricing section
  // Always show pricing section when product exists to display base price and calculated price
  get shouldShowPricingSection(): boolean {
    return this.productItem.product !== null;
  }

  get totalVariantQuantity(): number {
    return this.form.controls.variant_selections.controls.reduce((sum, ctrl) => {
      const rawQty = ctrl.get('quantity')?.value;
      const qty = typeof rawQty === 'number' ? rawQty : Number(rawQty);
      return sum + (isNaN(qty) ? 0 : qty);
    }, 0);
  }


  save() {
    if (!this.form) {
      return;
    }
    if (this.form.invalid) {
      return;
    }

    this.processing = true;

    // VG-395: For variant-based products, include ALL selections (even with quantity 0)
    // This allows saving variant-based products as "placeholder" with $0 contribution
    const variantSelections = this.form.value.variant_selections
      ?.filter(selection => {
        // Find the variant to check if it has options
        const variant = this.getVariantById(selection.variant_id!);
        const hasNoOptions = !variant?.options || variant.options.length === 0;

        // For 0-option variants (accommodation): always include with quantity > 0
        if (hasNoOptions) {
          return selection.quantity! > 0;
        }

        // For variants with options: filter out selections with no option selected
        if (selection.variant_option_id === null) {
          return false;
        }

        // For variant-based products: include all selections with options
        if (this.productItem.product.is_variant_based) {
          return true;
        }
        // For regular products with variants: only include selections with quantity > 0
        return selection.quantity! > 0;
      })
      .map(selection => ({
        // Don't include _uniqueId in the payload - it's only for UI tracking
        variant_id: selection.variant_id!,
        variant_option_id: selection.variant_option_id,
        quantity: selection.quantity!,
        notes: selection.notes,
      })) || [];

    const data: any = {
      quantity: this.form.value.quantity,
      is_paid: this.form.value.is_paid,
      event_timeline_item_id: this.form.value.event_timeline_item_id ?? null,
      variant_selections: variantSelections,
    };

    // TVG-69: on confirmed events the backend ignores `amount` and derives the
    // line total from the locked unit price. Skip the redundant payload.
    if (this.productItem.event_status !== 'Confirmed') {
      data.amount = this.form.value.amount;
    }

    this.itemUpdated.emit({ data, id: this.productItem.id });
  }

  cancel() {
    this.formCancelled.emit();
  }

  // Check if product is Venue Hire
  protected isVenueHireProduct(): boolean {
    return this.productItem.product.name.toLowerCase() === 'venue hire';
  }

  // Track by function for variant selections to avoid recreating DOM
  trackByUniqueId(index: number, selectionIndex: number): number {
    const control = this.form.controls.variant_selections.at(selectionIndex);
    return control?.value._uniqueId ?? selectionIndex;
  }

}
